import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaEye, FaEyeSlash, FaEnvelope, FaExclamationCircle } from "react-icons/fa";
import Modal from "../Popups/Modal";
import { loginStaff, forgotPassword } from "../api/auth.api";
import { saveLoginSession, getStaffId, STORAGE_KEYS, consumeSessionExpired } from "../utils/storage";

const LoginPage = () => {
  const [emailOrPhone, setEmailOrPhone] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [forgotEmail, setForgotEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [forgotError, setForgotError] = useState("");
  const [forgotSuccess, setForgotSuccess] = useState("");
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [termsError, setTermsError] = useState("");
  const [needsTermsAcceptance, setNeedsTermsAcceptance] = useState(true);
  const [altLogin, setAltLogin] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("reason") === "session" || consumeSessionExpired()) {
      setError("Your session has ended. Please sign in again.");
    }
    if (getStaffId()) navigate("/home", { replace: true });
    const acceptedTerms = localStorage.getItem(STORAGE_KEYS.acceptedTerms);
    if (acceptedTerms === "true") setNeedsTermsAcceptance(false);
  }, [navigate, searchParams]);

  const handleTermsClick = (e) => {
    e.preventDefault();
    window.open("https://healthcare-terms-policies.onrender.com/terms-and-conditions", "_blank");
  };

  const handlePrivacyClick = (e) => {
    e.preventDefault();
    window.open("https://healthcare-terms-policies.onrender.com/privacy-and-policy", "_blank");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setEmailError("");
    setTermsError("");
    setSubmitting(true);

    if (!emailOrPhone || !password) {
      setError("Please fill in both fields.");
      setSubmitting(false);
      return;
    }

    if (needsTermsAcceptance && !acceptTerms && altLogin) {
      setTermsError("You must accept the terms and conditions to continue.");
      setSubmitting(false);
      return;
    }

    const isEmail = emailOrPhone.includes("@");
    if (!altLogin && isEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailOrPhone)) {
      setEmailError("Please enter a valid work email address.");
      setSubmitting(false);
      return;
    }

    try {
      const loginData = {
        password,
        acceptTermsAndConditions: needsTermsAcceptance ? acceptTerms : false,
      };
      if (isEmail) loginData.email = emailOrPhone;
      else loginData.contact_number = emailOrPhone;

      const response = await loginStaff(loginData);
      if (response.status === 200) {
        const { staff, companyInfo, token } = response.data;
        if (staff.termsAndConditionsAccepted) setNeedsTermsAcceptance(false);
        saveLoginSession(staff, companyInfo, token);
        navigate("/home");
      }
    } catch (err) {
      if (err.response?.data?.message?.includes("Terms and conditions")) {
        setTermsError(err.response.data.message);
        setNeedsTermsAcceptance(true);
      } else if (!altLogin && emailOrPhone.includes("@")) {
        setEmailError("Please check your corporate email or contact IT support.");
      } else {
        setError("Invalid email/phone or password.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotError("");
    setForgotSuccess("");

    if (!forgotEmail || !newPassword) {
      setForgotError("Please fill in both fields.");
      return;
    }

    try {
      const response = await forgotPassword({ email: forgotEmail, newPassword });
      if (response.status === 200) {
        setForgotSuccess("Password updated successfully! You can now login.");
        setForgotEmail("");
        setNewPassword("");
        setTimeout(() => {
          setShowForgotModal(false);
          setForgotSuccess("");
        }, 1500);
      }
    } catch (err) {
      setForgotError(
        err.response?.data?.message || err.response?.data?.error || "Failed to reset password."
      );
    }
  };

  const inputBorder = emailError ? "border-error" : "border-surface-container";

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left — sign in form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 sm:px-12 lg:px-16 py-10 bg-surface-lowest">
        <div className="max-w-md w-full mx-auto">
          <div className="flex items-center gap-2 mb-8 sm:mb-10">
            <img src="/logo.png" alt="Elthium Health" className="w-10 h-10 object-contain" />
            <span className="font-bold text-xl text-primary">Elthium Health</span>
          </div>

          <div className="bg-transparent">
          <h1 className="text-3xl font-bold text-on-primary-fixed-var mb-2">Sign In</h1>
          <p className="text-on-surface-variant mb-8 text-sm leading-relaxed">
            Access your employee wellness portal via single sign-in.
          </p>

          {error && (
            <div className="bg-error-container/40 text-error p-3 mb-4 rounded-lg text-sm">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="emailOrPhone" className="block mb-1.5 font-semibold text-on-primary-fixed-var text-sm">
                {altLogin ? "Email or Phone Number" : "Work Email"}
              </label>
              <div className="relative">
                <FaEnvelope
                  className={`absolute left-3 top-1/2 -translate-y-1/2 ${emailError ? "text-error" : "text-on-surface-variant"}`}
                  size={16}
                />
                <input
                  type="text"
                  id="emailOrPhone"
                  value={emailOrPhone}
                  onChange={(e) => {
                    setEmailOrPhone(e.target.value);
                    setEmailError("");
                    setError("");
                  }}
                  placeholder={altLogin ? "Enter email or phone" : "employee@company.com"}
                  required
                  className={`w-full border-2 ${inputBorder} rounded-lg pl-10 pr-10 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 ${emailError ? "text-error" : ""}`}
                />
                {emailError && (
                  <FaExclamationCircle className="absolute right-3 top-1/2 -translate-y-1/2 text-error" size={16} />
                )}
              </div>
              {emailError && <p className="text-error text-xs mt-1.5">{emailError}</p>}
            </div>

            <div className="relative">
              <label htmlFor="password" className="block mb-1.5 font-semibold text-on-primary-fixed-var text-sm">
                Password
              </label>
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value.slice(0, 100))}
                placeholder="Enter your password"
                required
                maxLength={100}
                className="w-full border-2 border-surface-container rounded-lg px-3 py-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              <button
                type="button"
                className="absolute right-3 top-[45%] p-2 text-on-surface-variant hover:text-on-primary-fixed-var"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
              </button>
            </div>

            {altLogin && needsTermsAcceptance && (
              <div className="flex items-start pt-1">
                <input
                  type="checkbox"
                  id="terms"
                  checked={acceptTerms}
                  onChange={(e) => {
                    setAcceptTerms(e.target.checked);
                    if (e.target.checked) setTermsError("");
                  }}
                  className="mt-1 mr-3 h-4 w-4 text-primary rounded"
                />
                <div className="flex-1">
                  <label htmlFor="terms" className="text-sm text-on-primary-fixed-var cursor-pointer">
                    I agree to the{" "}
                    <a
                      href="https://healthcare-terms-policies.onrender.com/terms-and-conditions"
                      onClick={handleTermsClick}
                      className="text-primary font-semibold hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms and Conditions
                    </a>
                    {" and "}
                    <a
                      href="https://healthcare-terms-policies.onrender.com/privacy-and-policy"
                      onClick={handlePrivacyClick}
                      className="text-primary font-semibold hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </a>
                  </label>
                  {termsError && <p className="mt-1 text-error text-xs">{termsError}</p>}
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || !emailOrPhone || !password || (altLogin && needsTermsAcceptance && !acceptTerms)}
              className="w-full py-3.5 rounded-lg bg-primary hover:bg-primary-container text-white font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Signing in…" : "Sign In"}
            </button>
          </form>

          {/* <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => {
                setAltLogin(!altLogin);
                setEmailError("");
                setError("");
              }}
              className="text-primary font-semibold text-sm hover:underline"
            >
              {altLogin ? "Back to SSO Sign In" : "Alternative Login Options"}
            </button>
          </div> */}

          {altLogin && (
            <p className="text-sm text-center mt-3">
              <button
                type="button"
                className="text-primary hover:underline"
                onClick={() => setShowForgotModal(true)}
              >
                Forgot Password?
              </button>
            </p>
          )}
          </div>
        </div>
      </div>

      {/* Right — banner */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden bg-primary">
        <img
          src="/login-banner.png"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
      </div>

      <Modal open={showForgotModal} onClose={() => setShowForgotModal(false)} className="max-w-md">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4 text-on-primary-fixed-var pr-8">Reset Password</h2>
          {forgotError && (
            <div className="bg-error-container text-error p-2 mb-4 rounded-lg text-center text-sm">{forgotError}</div>
          )}
          {forgotSuccess && (
            <div className="bg-primary-fixed/40 text-primary p-2 mb-4 rounded-lg text-center text-sm">{forgotSuccess}</div>
          )}
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div>
              <label className="block mb-1 font-medium text-sm">Email</label>
              <input
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                required
                className="w-full border border-surface-container rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <div className="relative">
              <label className="block mb-1 font-medium text-sm">New Password</label>
              <input
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value.slice(0, 100))}
                required
                maxLength={100}
                className="w-full border border-surface-container rounded-lg px-3 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              <button
                type="button"
                className="absolute right-3 top-9 text-on-surface-variant"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
              </button>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                className="flex-1 py-2.5 border border-surface-container rounded-xl text-sm font-medium"
                onClick={() => setShowForgotModal(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 bg-primary text-white py-2.5 rounded-xl hover:bg-primary-container transition text-sm font-semibold"
              >
                Update Password
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
};

export default LoginPage;
